import React, { useEffect, useRef, useState } from "react";
import { Clock, Loader2, X } from "lucide-react";

// Go-Live Correction (Feb 2026) — Cash Payment Confirmation Workflow.
// Rendered instead of `OrderConfirmedModal` for dine-in / takeout cash
// orders that were placed via the "Pay at Counter" flow. The screen:
//   * clearly tells the customer that payment has NOT been confirmed;
//   * hides "Print Receipt" and "Start New Order" so the customer
//     cannot walk away with an unpaid receipt;
//   * polls the public order-status endpoint every `pollMs` ms until
//     staff marks the order as cash-received (payment_status="paid"),
//     at which point `onConfirmed` fires and the parent swaps this
//     modal for the standard `OrderConfirmedModal`;
//   * exposes a secondary "Cancel this order" gesture for cases like
//     "changed my mind" or "wrong table" — safe because the order has
//     not yet been paid or accepted into the kitchen. Cancel triggers
//     a two-step confirm to prevent accidental taps.
export default function AwaitingPaymentModal({
  tenant, order, slug, pollFn, pollMs = 4000, onConfirmed, onCancelled, cancelFn,
}) {
  const timerRef = useRef(null);
  const settledRef = useRef(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState("");

  useEffect(() => {
    if (!pollFn || !order?.order_code) return undefined;
    let cancelled = false;
    async function tick() {
      if (cancelled || settledRef.current) return;
      try {
        const fresh = await pollFn(order.order_code);
        if (cancelled || settledRef.current) return;
        const ps = String(fresh?.payment_status || "").toLowerCase();
        const st = String(fresh?.status || "").toLowerCase();
        const awaiting = !!fresh?.awaiting_staff_payment;
        // If staff cancelled the order while the customer was waiting,
        // don't loop forever — surface the cancellation to the parent.
        if (st === "cancelled") {
          settledRef.current = true;
          onCancelled?.(fresh, { source: "staff" });
          return;
        }
        if (ps === "paid" && !awaiting) {
          settledRef.current = true;
          onConfirmed?.(fresh);
          return;
        }
      } catch (_err) {
        // Silent — kiosk network hiccups shouldn't break the waiting UX.
      }
      timerRef.current = setTimeout(tick, pollMs);
    }
    timerRef.current = setTimeout(tick, pollMs);
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [order?.order_code, pollFn, pollMs, onConfirmed, onCancelled]);

  const doCancel = async () => {
    if (!cancelFn || !order?.order_code) return;
    setCancelError("");
    setCancelling(true);
    try {
      await cancelFn(order.order_code);
      settledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      onCancelled?.(null, { source: "customer" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === "object" && detail?.message)
        ? detail.message
        : (typeof detail === "string" ? detail : "Could not cancel order. Please ask staff for help.");
      setCancelError(msg);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center px-4 py-8 bg-black/70"
      data-testid="retail-awaiting-payment"
    >
      <div className="w-full max-w-md bg-white rounded-2xl border-t-4 border-amber-500 p-6 text-center text-stone-900 shadow-2xl">
        <div className="relative w-14 h-14 mx-auto">
          <Clock className="w-14 h-14 text-amber-500" />
          <Loader2 className="w-6 h-6 text-amber-500 animate-spin absolute -bottom-1 -right-1 bg-white rounded-full" />
        </div>
        <h2 className="font-serif text-3xl mt-3" data-testid="retail-awaiting-payment-title">
          Waiting for Payment Confirmation
        </h2>
        <p className="text-sm text-stone-500 mt-2" data-testid="retail-awaiting-payment-subtitle">
          Your order has been received. Please pay at the counter.
        </p>
        <p className="text-xs text-stone-500 mt-1">
          Your order will be confirmed after {tenant?.name || "our staff"} receives your payment.
        </p>
        <div className="mt-5 bg-amber-50 rounded-xl border border-amber-200 p-5">
          <div className="text-[10px] uppercase tracking-widest text-amber-700">Order number</div>
          <div
            className="font-mono text-3xl tracking-wider text-stone-900 mt-1"
            data-testid="retail-awaiting-payment-order-code"
          >
            {order?.order_code}
          </div>
          <div
            className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-800 bg-amber-100 rounded-full px-2 py-1"
            data-testid="retail-awaiting-payment-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            Awaiting staff confirmation
          </div>
        </div>
        <p className="mt-4 text-[11px] text-stone-400">
          Please keep this screen open — it will update automatically.
        </p>

        {/* Cancel gesture — two-step to prevent accidental taps. Only
            enabled while cancelFn is provided AND the order is still
            eligible (payment has NOT been recorded yet). Once staff
            has confirmed cash the poll will swap this modal out on
            the next tick, but as an extra guardrail we hide the
            cancel affordance the instant the polled snapshot flips
            `payment_status` to anything other than `pending`. */}
        {cancelFn && ((order?.payment_status || "pending").toLowerCase() === "pending") && (
          <div className="mt-5 pt-4 border-t border-stone-100">
            {!confirmingCancel ? (
              <button
                type="button"
                onClick={() => { setCancelError(""); setConfirmingCancel(true); }}
                data-testid="retail-awaiting-payment-cancel-btn"
                className="text-xs text-stone-500 hover:text-stone-700 underline underline-offset-4"
              >
                Need to cancel this order?
              </button>
            ) : (
              <div className="space-y-2" data-testid="retail-awaiting-payment-cancel-confirm">
                <p className="text-xs text-stone-600">
                  Cancel this order? You have not paid yet, so this is safe.
                </p>
                {cancelError && (
                  <p className="text-[11px] text-red-600" data-testid="retail-awaiting-payment-cancel-error">
                    {cancelError}
                  </p>
                )}
                <div className="flex gap-2 justify-center">
                  <button
                    type="button"
                    onClick={() => setConfirmingCancel(false)}
                    disabled={cancelling}
                    data-testid="retail-awaiting-payment-cancel-keep"
                    className="h-9 px-3 rounded-md border border-stone-300 text-xs text-stone-700 hover:bg-stone-50 disabled:opacity-50"
                  >
                    Keep order
                  </button>
                  <button
                    type="button"
                    onClick={doCancel}
                    disabled={cancelling}
                    data-testid="retail-awaiting-payment-cancel-confirm-btn"
                    className="h-9 px-3 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-60 inline-flex items-center gap-1.5"
                  >
                    {cancelling ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cancelling…
                      </>
                    ) : (
                      <>
                        <X className="w-3.5 h-3.5" /> Yes, cancel
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
